import { GIANT_LEVEL_ID_SUFFIX } from '../catalog/flightPhysics';
import { GIANT_ORDER_COUNT } from '../catalog/mission';
import type { MissionRef } from '../catalog/mission';
import { GIANT_IDS, isGiantId, type GiantId } from '../ids/ids';
import { isoDateFromMs } from '../time/moscowCalendar';
import {
  DELIVERY_ATTEMPTS_MAX,
  DELIVERY_POOL_MAX_SIZE,
  URGENT_DELIVERY_TTL_MS,
} from './constants';
import type { DeliveryPoolEntry, DeliveryUrgency, PlayerEconomyState } from './types';
import { deliveryIdFromMission, missionFromDeliveryId } from './types';

/** Слоты срочности дневной пачки (5 заказов). */
const DAILY_URGENCY_SLOTS: readonly DeliveryUrgency[] = [
  'urgent',
  'important',
  'important',
  'regular',
  'regular',
];

/** Гиганты, которых нужно всегда держать в дневной пачке (пусто = случайный набор). */
const ALWAYS_IN_DAILY_PACK_GIANTS: readonly GiantId[] = [];

function levelIdForGiant(giantId: GiantId): string {
  return `${giantId}${GIANT_LEVEL_ID_SUFFIX}`;
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(parts: readonly string[]): number {
  let h = 2166136261;
  for (const part of parts) {
    for (let i = 0; i < part.length; i += 1) {
      h ^= part.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
  }
  return h >>> 0;
}

function shuffleWithRng<T>(items: readonly T[], rng: () => number): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function giantsForDailyPack(): GiantId[] {
  return [...GIANT_IDS];
}

function pickGiantsForDailyPack(unlockedGiants: readonly GiantId[], rng: () => number): GiantId[] {
  return shuffleWithRng([...unlockedGiants], rng).slice(0, DELIVERY_POOL_MAX_SIZE);
}

function ensureAlwaysAvailableGiantsInPool(
  pool: DeliveryPoolEntry[],
  now: number,
): DeliveryPoolEntry[] {
  const giantsInPool = new Set(pool.map((entry) => entry.giantId));
  const missing = ALWAYS_IN_DAILY_PACK_GIANTS.filter((giantId) => !giantsInPool.has(giantId));
  if (missing.length === 0) {
    return pool;
  }

  let next = [...pool];
  for (const giantId of missing) {
    const replaceIdx = next.findIndex(
      (entry) =>
        isDailyPoolEntry(entry) &&
        !isPoolEntryFinished(entry) &&
        !ALWAYS_IN_DAILY_PACK_GIANTS.includes(entry.giantId),
    );
    if (replaceIdx < 0) {
      continue;
    }
    const replaced = next[replaceIdx];
    next[replaceIdx] = makeDeliveryPoolEntry(
      { giantId, orderIndex: 0 },
      now,
      replaced.urgency,
      replaced.waitingDay,
    );
  }
  return next;
}

function normalizeUrgency(entry: DeliveryPoolEntry): DeliveryUrgency {
  if (entry.urgency === 'urgent' || entry.urgency === 'important' || entry.urgency === 'regular') {
    return entry.urgency;
  }
  return entry.urgent === true ? 'urgent' : 'regular';
}

export function migratePoolEntry(entry: DeliveryPoolEntry, now?: number): DeliveryPoolEntry {
  const urgency = normalizeUrgency(entry);
  const backfillNow = now ?? entry.updatedAt ?? entry.createdAt;
  const fromId = missionFromDeliveryId(entry.deliveryId);
  const giantId = isGiantId(entry.giantId) ? entry.giantId : fromId?.giantId;
  const orderIndex = Number.isInteger(entry.orderIndex)
    ? entry.orderIndex
    : fromId?.orderIndex;
  const resolvedGiantId = giantId ?? entry.giantId;
  const resolvedOrderIndex = orderIndex ?? entry.orderIndex;
  return {
    ...entry,
    giantId: resolvedGiantId,
    orderIndex: resolvedOrderIndex,
    deliveryId:
      entry.deliveryId && entry.deliveryId.length > 0
        ? entry.deliveryId
        : deliveryIdFromMission({ giantId: resolvedGiantId, orderIndex: resolvedOrderIndex }),
    urgency,
    waitingDay: Math.max(1, Math.floor(entry.waitingDay ?? 1)),
    takenOnWaitingDay:
      typeof entry.takenOnWaitingDay === 'number'
        ? entry.takenOnWaitingDay
        : typeof entry.urgentRewardClaimed === 'boolean' && entry.urgent === true
          ? entry.waitingDay ?? 1
          : null,
    passRewardClaimed: entry.passRewardClaimed === true || entry.urgentRewardClaimed === true,
    urgent: urgency === 'urgent',
    urgentExpiresAt:
      urgency === 'urgent'
        ? entry.urgentExpiresAt ?? backfillNow + URGENT_DELIVERY_TTL_MS
        : null,
  };
}

export function makeDeliveryPoolEntry(
  mission: MissionRef,
  now: number,
  urgency: DeliveryUrgency = 'regular',
  waitingDay = 1,
): DeliveryPoolEntry {
  return {
    deliveryId: deliveryIdFromMission(mission),
    levelId: levelIdForGiant(mission.giantId),
    giantId: mission.giantId,
    orderIndex: mission.orderIndex,
    createdAt: now,
    updatedAt: now,
    attemptsLeft: DELIVERY_ATTEMPTS_MAX,
    bestRating: 0,
    status: 'active',
    urgency,
    waitingDay: Math.max(1, waitingDay),
    takenOnWaitingDay: null,
    passRewardClaimed: false,
    urgent: urgency === 'urgent',
    urgentExpiresAt: urgency === 'urgent' ? now + URGENT_DELIVERY_TTL_MS : null,
  };
}

function isDailyPoolEntry(entry: DeliveryPoolEntry): boolean {
  return entry.fromHistoryReplay !== true;
}

function dailyPoolEntries(pool: readonly DeliveryPoolEntry[]): DeliveryPoolEntry[] {
  return pool.filter(isDailyPoolEntry);
}

function historyReplayEntries(pool: readonly DeliveryPoolEntry[]): DeliveryPoolEntry[] {
  return pool.filter((entry) => entry.fromHistoryReplay === true);
}

function pruneInactiveHistoryReplayEntries(
  pool: readonly DeliveryPoolEntry[],
): DeliveryPoolEntry[] {
  return pool.filter((entry) => !entry.fromHistoryReplay || entry.status === 'active');
}

/** Shadow-запись пула для повтора заказа из истории вне дневной пачки. */
export function makeHistoryReplayPoolEntry(
  history: {
    giantId: GiantId;
    orderIndex: number;
    bestRating: number;
  },
  now: number,
): DeliveryPoolEntry {
  const base = makeDeliveryPoolEntry(
    { giantId: history.giantId, orderIndex: history.orderIndex },
    now,
    'regular',
    1,
  );
  return {
    ...base,
    bestRating: history.bestRating,
    status: history.bestRating > 0 ? 'completed' : 'failed',
    fromHistoryReplay: true,
    urgent: false,
    urgentExpiresAt: null,
  };
}

/** Успешно завершён (хотя бы одно прохождение). */
export function isPoolEntryDelivered(entry: DeliveryPoolEntry): boolean {
  return entry.status === 'completed' || entry.bestRating > 0;
}

/** Заказ закрыт: успех или провал (на карте больше не висит как новая доставка). */
export function isPoolEntryFinished(entry: DeliveryPoolEntry): boolean {
  return isPoolEntryDelivered(entry) || entry.status === 'failed';
}

/**
 * First unfinished daily-pack giant to speculative-preload (≠ exclude).
 * Never returns history-replay entries.
 */
export function pickNextPoolGiantToPreload(
  pool: readonly DeliveryPoolEntry[],
  excludeGiantId: GiantId | null = null,
): GiantId | null {
  for (const entry of dailyPoolEntries(pool)) {
    if (isPoolEntryFinished(entry)) {
      continue;
    }
    if (excludeGiantId != null && entry.giantId === excludeGiantId) {
      continue;
    }
    return entry.giantId;
  }
  return null;
}

/** Дневных заказов не больше DELIVERY_POOL_MAX_SIZE; лишние слоты снимаются. */
function capDailyPoolEntries(pool: readonly DeliveryPoolEntry[]): DeliveryPoolEntry[] {
  const daily = dailyPoolEntries(pool);
  if (daily.length <= DELIVERY_POOL_MAX_SIZE) {
    return [...pool];
  }
  const finished = daily.filter(isPoolEntryFinished);
  const unfinished = daily.filter((entry) => !isPoolEntryFinished(entry));
  return [...finished, ...unfinished]
    .slice(0, DELIVERY_POOL_MAX_SIZE)
    .concat(historyReplayEntries(pool));
}

/** Все заказы дневной пачки закрыты (успех или провал). */
export function isDailyPackComplete(pool: DeliveryPoolEntry[]): boolean {
  const daily = dailyPoolEntries(pool);
  if (daily.length < DELIVERY_POOL_MAX_SIZE) {
    return false;
  }
  return daily.every(isPoolEntryFinished);
}

/** Показывать блокирующий оверлей, пока дневная пачка полностью пройдена. */
export function shouldShowDailyPackCompleteModal(
  state: PlayerEconomyState,
): boolean {
  if (!state.deliveryPoolExhausted) {
    return false;
  }
  if (!isDailyPackComplete(state.deliveryPool)) {
    return false;
  }
  return state.deliveryPoolDate != null;
}

export function pickDailyPackMissions(
  unlockedGiants: readonly GiantId[],
  rng: () => number,
): { mission: MissionRef; urgency: DeliveryUrgency }[] {
  if (unlockedGiants.length < DELIVERY_POOL_MAX_SIZE) {
    return [];
  }
  const giants = pickGiantsForDailyPack(unlockedGiants, rng);
  const urgencies = shuffleWithRng(DAILY_URGENCY_SLOTS, rng);
  return giants.map((giantId, idx) => {
    const maxOrders = GIANT_ORDER_COUNT[giantId];
    const orderIndex = Math.floor(rng() * maxOrders);
    return {
      mission: { giantId, orderIndex },
      urgency: urgencies[idx],
    };
  });
}

export type DeliveryPoolSyncOptions = {
  /** Extra entropy for pack RNG (e.g. admin reset). Omitted = deterministic seed. */
  packSeedExtra?: string;
};

export function buildDailyPackEntries(
  state: PlayerEconomyState,
  now: number,
  packDate: string,
  options?: DeliveryPoolSyncOptions,
): DeliveryPoolEntry[] {
  const unlocked = giantsForDailyPack();
  const seedParts = [
    packDate,
    String(state.perfectDeliveriesCount),
    String(state.ratedDeliveriesCount),
  ];
  if (options?.packSeedExtra) {
    seedParts.push(options.packSeedExtra);
  }
  const seed = hashSeed(seedParts);
  const rng = mulberry32(seed);
  const picks = pickDailyPackMissions(unlocked, rng);
  return picks.map(({ mission, urgency }) => makeDeliveryPoolEntry(mission, now, urgency, 1));
}

function mergeOpenedMapGiantIds(
  existing: readonly GiantId[] | undefined,
  giantIds: Iterable<GiantId | string>,
): GiantId[] {
  const set = new Set(existing ?? []);
  for (const id of giantIds) {
    if (isGiantId(id)) {
      set.add(id);
    }
  }
  return [...set];
}

/** Участок карты цветной, если гигант когда-либо появлялся в пуле заказов. */
export function isMapSectionColored(
  state: PlayerEconomyState,
  giantId: GiantId,
): boolean {
  return (state.openedMapGiantIds ?? []).includes(giantId);
}

/** Миграция: seed openedMapGiantIds из текущего пула и истории. */
export function seedOpenedMapGiantIds(state: PlayerEconomyState): PlayerEconomyState {
  if ((state.openedMapGiantIds?.length ?? 0) > 0) {
    return state;
  }
  const fromPool = state.deliveryPool.map((e) => e.giantId);
  const fromHistory = (state.deliveryHistory ?? []).map((e) => e.giantId);
  const opened = mergeOpenedMapGiantIds(undefined, [...fromPool, ...fromHistory]);
  if (opened.length === 0) {
    return state;
  }
  return { ...state, openedMapGiantIds: opened };
}

function withOpenedGiantsFromPool(
  state: PlayerEconomyState,
  pool: readonly DeliveryPoolEntry[],
): PlayerEconomyState {
  return {
    ...state,
    openedMapGiantIds: mergeOpenedMapGiantIds(
      state.openedMapGiantIds,
      pool.map((e) => e.giantId),
    ),
  };
}

function issueNewDailyPack(
  state: PlayerEconomyState,
  now: number,
  packDate: string,
  options?: DeliveryPoolSyncOptions,
): PlayerEconomyState {
  const shadows = state.deliveryPool.filter(
    (entry) => entry.fromHistoryReplay === true && entry.status === 'active',
  );
  const pack = buildDailyPackEntries(state, now, packDate, options).slice(
    0,
    DELIVERY_POOL_MAX_SIZE,
  );
  const pool = [...pack, ...shadows];
  return withOpenedGiantsFromPool(
    {
      ...state,
      deliveryPool: pool,
      deliveryPoolDate: packDate,
      deliveryPoolExhausted: false,
      deliveryPackCompleteAckDate: null,
      lastPoolRotationAt: now,
    },
    pool,
  );
}

function calendarDaysBetween(fromDate: string, toDate: string): number {
  const from = Date.parse(fromDate);
  const to = Date.parse(toDate);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return 1;
  }
  return Math.max(1, Math.floor((to - from) / (24 * 60 * 60 * 1000)));
}

function bumpWaitingDaysOnCarry(
  entries: readonly DeliveryPoolEntry[],
  daysPassed: number,
): DeliveryPoolEntry[] {
  return entries.map((entry) => ({
    ...entry,
    waitingDay: Math.max(1, entry.waitingDay + daysPassed),
  }));
}

/** Сколько слотов каждой срочности ещё нужно, чтобы приблизиться к DAILY_URGENCY_SLOTS. */
function urgencySlotsForTopUp(
  kept: readonly DeliveryPoolEntry[],
  count: number,
): DeliveryUrgency[] {
  const remaining: Record<DeliveryUrgency, number> = {
    urgent: 1,
    important: 2,
    regular: 2,
  };
  for (const entry of kept) {
    remaining[entry.urgency] = Math.max(0, remaining[entry.urgency] - 1);
  }
  const slots: DeliveryUrgency[] = [];
  for (const urgency of ['urgent', 'important', 'regular'] as const) {
    for (let i = 0; i < remaining[urgency]; i += 1) {
      slots.push(urgency);
    }
  }
  while (slots.length < count) {
    slots.push('regular');
  }
  return slots.slice(0, count);
}

function buildTopUpEntries(
  state: PlayerEconomyState,
  now: number,
  packDate: string,
  kept: readonly DeliveryPoolEntry[],
  count: number,
): DeliveryPoolEntry[] {
  if (count <= 0) {
    return [];
  }
  const excludeGiants = new Set(kept.map((entry) => entry.giantId));
  const available = giantsForDailyPack().filter((id) => !excludeGiants.has(id));
  const seed = hashSeed([
    packDate,
    'topup',
    String(state.perfectDeliveriesCount),
    String(state.ratedDeliveriesCount),
    ...kept.map((entry) => entry.deliveryId).sort(),
  ]);
  const rng = mulberry32(seed);
  const urgencies = shuffleWithRng(urgencySlotsForTopUp(kept, count), rng);
  const giants = shuffleWithRng(available, rng).slice(0, count);
  return giants.map((giantId, idx) => {
    const maxOrders = GIANT_ORDER_COUNT[giantId];
    const orderIndex = Math.floor(rng() * maxOrders);
    return makeDeliveryPoolEntry(
      { giantId, orderIndex },
      now,
      urgencies[idx] ?? 'regular',
      1,
    );
  });
}

/**
 * Смена дня: невзятые заказы остаются (waitingDay++), закрытые (успех или провал)
 * снимаются, пачка дополняется новыми до DELIVERY_POOL_MAX_SIZE.
 * Если незакрытых нет — новая пачка.
 */
function advanceDailyPack(
  state: PlayerEconomyState,
  now: number,
  packDate: string,
): PlayerEconomyState {
  const shadows = state.deliveryPool.filter(
    (entry) => entry.fromHistoryReplay === true && entry.status === 'active',
  );
  const incomplete = dailyPoolEntries(state.deliveryPool).filter(
    (entry) => !isPoolEntryFinished(entry),
  );

  if (incomplete.length === 0) {
    return issueNewDailyPack(state, now, packDate);
  }

  const oldDate = state.deliveryPoolDate ?? packDate;
  const daysPassed = calendarDaysBetween(oldDate, packDate);
  const kept = bumpWaitingDaysOnCarry(incomplete, daysPassed).slice(
    0,
    DELIVERY_POOL_MAX_SIZE,
  );
  const topUp = buildTopUpEntries(
    state,
    now,
    packDate,
    kept,
    Math.max(0, DELIVERY_POOL_MAX_SIZE - kept.length),
  );
  const pool = [...kept, ...topUp, ...shadows];

  return withOpenedGiantsFromPool(
    {
      ...state,
      deliveryPool: pool,
      deliveryPoolDate: packDate,
      deliveryPoolExhausted: false,
      deliveryPackCompleteAckDate: null,
      lastPoolRotationAt: now,
    },
    pool,
  );
}

function pickReplacementOrderIndex(
  giantId: GiantId,
  excludeIndex: number,
  rng: () => number,
): number {
  const maxOrders = GIANT_ORDER_COUNT[giantId];
  if (maxOrders <= 1) {
    return 0;
  }
  let orderIndex = Math.floor(rng() * maxOrders);
  if (orderIndex === excludeIndex) {
    orderIndex = (orderIndex + 1) % maxOrders;
  }
  return orderIndex;
}

function expireUrgentEntries(
  pool: DeliveryPoolEntry[],
  packDate: string,
  now: number,
): DeliveryPoolEntry[] {
  return pool.map((entry) => {
    if (entry.status !== 'active' || entry.urgency !== 'urgent') {
      return entry;
    }
    const expiresAt = entry.urgentExpiresAt;
    if (expiresAt == null || now < expiresAt) {
      return entry;
    }
    const seed = hashSeed([
      packDate,
      entry.giantId,
      String(Math.floor(now / URGENT_DELIVERY_TTL_MS)),
    ]);
    const rng = mulberry32(seed);
    const orderIndex = pickReplacementOrderIndex(entry.giantId, entry.orderIndex, rng);
    return makeDeliveryPoolEntry(
      { giantId: entry.giantId, orderIndex },
      now,
      'urgent',
      entry.waitingDay,
    );
  });
}

/**
 * Синхронизация дневной пачки: не больше 5 заказов в календарный день.
 * Новая пачка — только если день сменился или пачки ещё не было.
 * В тот же день слоты не добираются и не перевыпускаются.
 */
export function syncDeliveryPool(
  state: PlayerEconomyState,
  now: number,
  options?: DeliveryPoolSyncOptions,
): PlayerEconomyState {
  const today = isoDateFromMs(now);
  let next: PlayerEconomyState = {
    ...state,
    deliveryPool: pruneInactiveHistoryReplayEntries(
      state.deliveryPool.map((entry) => migratePoolEntry(entry, now)),
    ),
  };

  const daily = dailyPoolEntries(next.deliveryPool);
  const packDate = next.deliveryPoolDate ?? null;

  if (daily.length === 0 && (packDate == null || packDate < today)) {
    return issueNewDailyPack(next, now, today, options);
  }

  if (packDate != null && packDate < today) {
    next = advanceDailyPack(next, now, today);
  } else {
    next = {
      ...next,
      deliveryPool: capDailyPoolEntries(next.deliveryPool),
      deliveryPoolDate: packDate ?? today,
    };
  }

  const poolDate = next.deliveryPoolDate ?? today;

  next = {
    ...next,
    deliveryPool: expireUrgentEntries(next.deliveryPool, poolDate, now),
  };

  next = {
    ...next,
    deliveryPool: ensureAlwaysAvailableGiantsInPool(next.deliveryPool, now),
  };

  next = withOpenedGiantsFromPool(next, next.deliveryPool);

  if (isDailyPackComplete(next.deliveryPool)) {
    next = { ...next, deliveryPoolExhausted: true };
  }

  return next;
}

/** @deprecated Используйте {@link syncDeliveryPool}. */
export function rotateDeliveryPoolIfNeeded(
  state: PlayerEconomyState,
  now: number,
): PlayerEconomyState {
  return syncDeliveryPool(state, now);
}

export function createInitialPlayerEconomy(
  now: number,
  options?: DeliveryPoolSyncOptions,
): PlayerEconomyState {
  const state: PlayerEconomyState = {
    totalRating: 0,
    ratingSum: 0,
    ratedDeliveriesCount: 0,
    perfectDeliveriesCount: 0,
    passesBalance: 0,
    streakDays: 0,
    lastLoginDate: null,
    lastDailyRewardClaimedDate: null,
    dailyLoginStreakCompleted: false,
    urgentCompletedCount: 0,
    stickers: [],
    answeredQuizQuestionIds: [],
    quizDailyDate: null,
    quizDailyAnsweredCount: 0,
    notificationsRewardClaimed: false,
    vkSubscribeRewardClaimed: false,
    deliveryPool: [],
    deliveryPoolDate: null,
    deliveryPoolExhausted: false,
    deliveryPackCompleteAckDate: null,
    deliveryHistory: [],
    openedMapGiantIds: [],
    lastPoolRotationAt: now,
  };
  return syncDeliveryPool(state, now, options);
}

/** Запись остаётся в пуле до смены дневной пачки. */
export function shouldKeepPoolEntry(entry: DeliveryPoolEntry): boolean {
  return true;
}

/**
 * Добавить/обновить запись: при совпадении deliveryId обновляет данные.
 */
export function upsertPoolEntry(
  pool: DeliveryPoolEntry[],
  entry: DeliveryPoolEntry,
): DeliveryPoolEntry[] {
  const idx = pool.findIndex((e) => e.deliveryId === entry.deliveryId);
  if (idx >= 0) {
    const next = [...pool];
    next[idx] = { ...entry, createdAt: pool[idx].createdAt, updatedAt: entry.updatedAt };
    return next;
  }
  if (
    entry.fromHistoryReplay === true ||
    dailyPoolEntries(pool).length < DELIVERY_POOL_MAX_SIZE
  ) {
    return [...pool, migratePoolEntry(entry, entry.updatedAt)];
  }
  return pool;
}

export function findPoolEntry(
  state: PlayerEconomyState,
  deliveryId: string,
): DeliveryPoolEntry | null {
  return state.deliveryPool.find((e) => e.deliveryId === deliveryId) ?? null;
}

export function findPoolEntryByGiant(
  state: PlayerEconomyState,
  giantId: GiantId,
): DeliveryPoolEntry | null {
  return state.deliveryPool.find((e) => e.giantId === giantId) ?? null;
}

/** Есть ли у гиганта заказ, доступный к взятию на карте. */
export function hasActiveDeliveryForGiant(
  pool: readonly DeliveryPoolEntry[],
  giantId: GiantId,
): boolean {
  return pool.some(
    (entry) =>
      entry.giantId === giantId &&
      entry.status === 'active' &&
      entry.fromHistoryReplay !== true,
  );
}

export function ensureMissionInPool(
  state: PlayerEconomyState,
  mission: MissionRef,
  now: number,
): PlayerEconomyState {
  const deliveryId = deliveryIdFromMission(mission);
  if (findPoolEntry(state, deliveryId) != null) {
    return state;
  }
  const history = (state.deliveryHistory ?? []).find((e) => e.deliveryId === deliveryId);
  if (history == null) {
    return state;
  }
  const entry = makeHistoryReplayPoolEntry(history, now);
  const deliveryPool = [...state.deliveryPool, entry];
  return withOpenedGiantsFromPool({ ...state, deliveryPool }, deliveryPool);
}

export function acknowledgeDailyPackComplete(
  state: PlayerEconomyState,
  today = isoDateFromMs(Date.now()),
): PlayerEconomyState {
  return {
    ...state,
    deliveryPackCompleteAckDate: state.deliveryPoolDate ?? today,
  };
}

export function afterDeliverySettled(
  state: PlayerEconomyState,
  now: number,
): PlayerEconomyState {
  let next = { ...state };
  if (isDailyPackComplete(next.deliveryPool)) {
    next = { ...next, deliveryPoolExhausted: true };
  }
  return syncDeliveryPool(next, now);
}
